import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { getOrder } from "../lib/api";
import { formatMoneyDecimal } from "../lib/categories";
import SmartImg from "../components/SmartImg";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);

  useEffect(() => {
    if (order) return;
    let mounted = true;
    setLoading(true);
    getOrder(orderNumber)
      .then((o) => mounted && setOrder(o))
      .catch(() => mounted && setOrder(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [orderNumber, order]);

  if (loading) {
    return (
      <main className="mx-auto max-w-[900px] px-5 lg:px-10 py-24 flex items-center justify-center text-neutral-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Loading order…
      </main>
    );
  }

  if (!order) {
    return (
      <main className="mx-auto max-w-[900px] px-5 lg:px-10 py-24 text-center">
        <h1 className="font-display font-bold text-3xl tracking-tight">
          Order not found
        </h1>
        <Link
          to="/"
          className="mt-6 inline-block text-sm underline underline-offset-4"
        >
          Back to home
        </Link>
      </main>
    );
  }

  return (
    <main
      className="mx-auto max-w-[900px] px-5 lg:px-10 py-14"
      data-testid="order-confirmation-page"
    >
      <div className="rounded-2xl border border-neutral-200 bg-white p-8 lg:p-12">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700">
            <CheckCircle2 size={24} strokeWidth={1.75} />
          </span>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              Order confirmed
            </div>
            <div
              className="font-display font-bold text-2xl tracking-tight"
              data-testid="order-number"
            >
              #{order.orderNumber}
            </div>
          </div>
        </div>

        <h1 className="mt-8 font-display font-bold text-4xl sm:text-5xl tracking-tighter">
          Thanks for the fictional purchase.
        </h1>
        <p className="mt-3 text-neutral-600 leading-relaxed">
          Your imaginary order has been placed. A confirmation would normally be
          emailed to <span className="font-medium text-neutral-900">{order.shipping?.email}</span>,
          but this is a demo, so nothing was actually sent.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <section>
            <h3 className="font-display text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Shipping to
            </h3>
            <div className="text-sm text-neutral-800 leading-relaxed">
              <div className="font-medium">{order.shipping?.fullName}</div>
              <div>{order.shipping?.address1}</div>
              {order.shipping?.address2 && <div>{order.shipping.address2}</div>}
              <div>
                {order.shipping?.city}, {order.shipping?.state}{" "}
                {order.shipping?.postalCode}
              </div>
              <div>{order.shipping?.country}</div>
              {order.shipping?.phone && (
                <div className="text-neutral-500 mt-1">{order.shipping.phone}</div>
              )}
            </div>
          </section>
          <section>
            <h3 className="font-display text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Totals
            </h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between">
                <dt className="text-neutral-600">Subtotal</dt>
                <dd>{formatMoneyDecimal(order.subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Shipping</dt>
                <dd>{formatMoneyDecimal(order.shippingFee)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Tax</dt>
                <dd>{formatMoneyDecimal(order.tax)}</dd>
              </div>
              <div className="flex justify-between pt-2 border-t border-neutral-200 font-display font-semibold text-base">
                <dt>Total</dt>
                <dd data-testid="order-total">{formatMoneyDecimal(order.total)}</dd>
              </div>
            </dl>
          </section>
        </div>

        <section className="mt-10">
          <h3 className="font-display text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Items ({order.items?.length})
          </h3>
          <ul className="divide-y divide-neutral-200 border border-neutral-200 rounded-xl" data-testid="order-items">
            {order.items?.map((i) => (
              <li key={i.productId} className="flex items-center gap-3 p-3">
                <SmartImg
                  src={i.image}
                  alt={i.title}
                  className="h-14 w-14 rounded-md shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">
                    {i.title}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {i.categoryLabel} · Qty {i.quantity}
                  </div>
                </div>
                <div className="text-sm font-medium text-neutral-900">
                  {formatMoneyDecimal(i.price * i.quantity)}
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
            data-testid="order-continue-shopping"
          >
            Keep shopping
          </Link>
        </div>
      </div>
    </main>
  );
}
